import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Run `load()` when `deps` change (and on `reload()`), keeping the last good data while refreshing.
 * `pollMs` re-runs it quietly on an interval.
 */
export default function useAsync(load, deps = [], { pollMs = 0, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(Boolean(enabled));
  const runId = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  const run = useCallback(
    async ({ quiet = false } = {}) => {
      if (!enabled) return undefined;
      const id = ++runId.current;
      if (!quiet) setLoading(true);
      try {
        const result = await loadRef.current();
        if (id === runId.current) {
          setData(result);
          setError('');
        }
        return result;
      } catch (e) {
        if (id === runId.current) setError(e.message || 'Something went wrong');
        return undefined;
      } finally {
        if (id === runId.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, ...deps],
  );

  useEffect(() => {
    run();
  }, [run]);

  useEffect(() => {
    if (!pollMs || !enabled) return undefined;
    const t = setInterval(() => run({ quiet: true }), pollMs);
    return () => clearInterval(t);
  }, [run, pollMs, enabled]);

  return { data, setData, error, loading, reload: run };
}
